import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, Store, ofActionDispatched } from '@ngxs/store';
import { Subject, switchMap, takeUntil, catchError, EMPTY } from 'rxjs';
import { NotifyService } from '../notification/notify.service';
import { Auth } from '../../store/auth/auth.actions';
import { AuthState } from '../../store/auth/auth.state';
import { UserActions } from '../../store/users/user.actions';
import { PermissionUtils, ModuleType } from '../../utils/permission.utils';

@Injectable({ providedIn: 'root' })
export class SessionSyncService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();

  private readonly routeModuleMap: Record<string, ModuleType> = {
    '/admin/users': ModuleType.GENERAL,
    '/admin/roles': ModuleType.GENERAL,
    '/admin/envios': ModuleType.LOGISTICS,
    '/admin/devoluciones': ModuleType.LOGISTICS,
    '/admin/conductores': ModuleType.LOGISTICS,
    '/admin/farmacias': ModuleType.LOGISTICS,
    '/admin/home': ModuleType.LOGISTICS,
    '/admin/atencion-cliente': ModuleType.CUSTOMER_SERVICE,
    '/admin/coordinacion': ModuleType.CUSTOMER_SERVICE,
  };

  // Permiso específico requerido por cada ruta
  private readonly routePermissionMap: Record<string, string> = {
    '/admin/users': 'users_read',
    '/admin/roles': 'roles_read',
    '/admin/home': 'dashboard_read',
    '/admin/envios': 'web_envios_read',
    '/admin/devoluciones': 'web_devoluciones_read',
    '/admin/conductores': 'conductores_read',
    '/admin/farmacias': 'farmacias_read',
    '/admin/atencion-cliente': 'ac_inicio_read',
    '/admin/atencion-cliente/importar-pdf': 'ac_ocr_read',
    '/admin/atencion-cliente/validar-pdf': 'ac_ocr_read',
    '/admin/coordinacion': 'ac_tablero_read',
  };

  constructor(
    private actions$: Actions,
    private store: Store,
    private router: Router,
    private permissionUtils: PermissionUtils,
    private notify: NotifyService,
  ) {}

  init(): void {
    this.actions$.pipe(
      ofActionDispatched(Auth.PermissionsChanged),
      switchMap(() =>
        this.store.dispatch(new UserActions.GetMe()).pipe(
          catchError(() => EMPTY),
        ),
      ),
      takeUntil(this.destroy$),
    ).subscribe(() => {
      this.handlePostRefresh();
    });
  }

  private handlePostRefresh(): void {
    const currentUrl = this.router.url;
    const userPermissions = this.store.selectSnapshot(AuthState.getPermissions);
    const authUser = this.store.selectSnapshot(AuthState.getUser);

    if (authUser?.isSuperAdmin) return;

    // Chequeo 1: permiso específico para la ruta actual
    const requiredPermission = this.resolvePermissionFromUrl(currentUrl);
    if (requiredPermission && !userPermissions.includes(requiredPermission)) {
      this.redirectWithToast(true);
      return;
    }

    // Chequeo 2: acceso al módulo completo
    const currentModule = this.resolveModuleFromUrl(currentUrl);
    if (!currentModule) return;
    if (this.permissionUtils.canAccessModule(currentModule)) return;

    this.redirectWithToast(false);
  }

  private redirectWithToast(pageLevel: boolean): void {
    const message = pageLevel
      ? 'Tu acceso a esta sección fue modificado.'
      : 'Tu acceso a este módulo fue modificado.';

    this.notify.show({ tipo: 'warn', titulo: message, desc: 'Serás redirigido en unos segundos.', duracion: 3000 });

    setTimeout(() => this.router.navigate(['/modulos']), 3000);
  }

  private resolvePermissionFromUrl(url: string): string | null {
    const cleanUrl = url.split('?')[0];
    if (this.routePermissionMap[cleanUrl]) return this.routePermissionMap[cleanUrl];
    // Buscar por startsWith de más específico a menos
    const sortedRoutes = Object.keys(this.routePermissionMap).sort((a, b) => b.length - a.length);
    for (const route of sortedRoutes) {
      if (cleanUrl.startsWith(route)) return this.routePermissionMap[route];
    }
    return null;
  }

  private resolveModuleFromUrl(url: string): ModuleType | null {
    if (this.routeModuleMap[url]) return this.routeModuleMap[url];
    for (const [route, module] of Object.entries(this.routeModuleMap)) {
      if (url.startsWith(route)) return module;
    }
    return null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
